<script setup>
/**
 * GviaSuccessPage
 * Success page shown after successful payment
 */
import { useRouter } from 'vue-router'
import AppButton from '@/ui/components/AppButton.vue'
import { ROUTES } from '@/core/config/constants'

const router = useRouter()

function handleBack() {
  router.replace({ name: ROUTES.AGENTS_GVIA })
}
</script>

<template>
  <div class="success-page">
    <div class="success-content">
      <!-- Success Icon -->
      <div class="success-icon">
        <svg
          viewBox="0 0 24 24"
          width="80"
          height="80"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="8 12 11 15 16 9" />
        </svg>
      </div>

      <!-- Success Message -->
      <h1 class="success-title">העסקה בוצעה בהצלחה!</h1>
      <p class="success-message">התשלום התקבל ואושר במערכת</p>

      <!-- Decorative Elements -->
      <div class="confetti">
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
      </div>

      <!-- Back Button -->
      <AppButton variant="success" size="lg" class="back-btn" @click="handleBack">
        חזרה לגבייה
      </AppButton>
    </div>
  </div>
</template>

<style scoped>
.success-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: var(--spacing-xl);
  background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
}

.success-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 400px;
  position: relative;
}

/* Success Icon */
.success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, var(--color-success) 0%, #059669 100%);
  border-radius: 50%;
  color: white;
  margin-bottom: var(--spacing-xl);
  animation:
    scaleIn 0.5s ease-out,
    pulse 2s ease-in-out infinite 0.5s;
  box-shadow: 0 10px 40px rgba(34, 197, 94, 0.3);
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 10px 40px rgba(34, 197, 94, 0.3);
  }
  50% {
    box-shadow: 0 10px 60px rgba(34, 197, 94, 0.5);
  }
}

/* Text */
.success-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-sm) 0;
  animation: fadeInUp 0.5s ease-out 0.2s both;
}

.success-message {
  font-size: var(--font-size-base);
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-xl) 0;
  animation: fadeInUp 0.5s ease-out 0.3s both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Back Button */
.back-btn {
  min-width: 200px;
  animation: fadeInUp 0.5s ease-out 0.4s both;
}

/* Confetti */
.confetti {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
}

.confetti span {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  animation: confettiFall 3s ease-out infinite;
}

.confetti span:nth-child(1) {
  background: #f59e0b;
  left: -60px;
  animation-delay: 0s;
}

.confetti span:nth-child(2) {
  background: #ef4444;
  left: -30px;
  animation-delay: 0.2s;
}

.confetti span:nth-child(3) {
  background: #334155;
  left: 0;
  animation-delay: 0.4s;
}

.confetti span:nth-child(4) {
  background: #3b82f6;
  left: 30px;
  animation-delay: 0.6s;
}

.confetti span:nth-child(5) {
  background: #22c55e;
  left: 60px;
  animation-delay: 0.8s;
}

@keyframes confettiFall {
  0% {
    opacity: 1;
    transform: translateY(0) rotate(0deg);
  }
  100% {
    opacity: 0;
    transform: translateY(150px) rotate(720deg);
  }
}

/* Responsive */
@media (max-width: 480px) {
  .success-page {
    padding: var(--spacing-md);
  }

  .success-icon {
    width: 100px;
    height: 100px;
  }

  .success-icon svg {
    width: 60px;
    height: 60px;
  }

  .success-title {
    font-size: var(--font-size-xl);
  }
}
</style>
